import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Footer, MarketingHeader, SEO } from '@/components';
import { CHECK_QUIZZES, type CheckSlug } from '@/lib/check/quizzes';
import { API_BASE_URL } from '@/lib/api';
import { track } from '@/lib/analytics';

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

export const Route = createFileRoute('/check/$quiz/')({
  component: CheckQuizPage,
});

function CheckQuizPage() {
  const { quiz: quizSlug } = Route.useParams();
  const slug = quizSlug as CheckSlug;
  const quiz = CHECK_QUIZZES[slug];
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const question = quiz.questions[step];
  const total = quiz.questions.length;

  useEffect(() => {
    track('Check Quiz Screen', { quiz: slug, step: question.key });
  }, [slug, question.key]);

  const submit = async (nextAnswers: Record<string, string>) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/v1/checks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quiz_slug: slug, answers: nextAnswers }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { detail?: string };
        throw new Error(body.detail || 'Could not save your check.');
      }
      const data = (await res.json()) as { public_id: string };
      await navigate({
        to: '/check/$quiz/r/$publicId',
        params: { quiz: slug, publicId: data.public_id },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your check.');
      setSubmitting(false);
    }
  };

  const onPick = (key: string) => {
    const next = { ...answers, [question.key]: key };
    setAnswers(next);
    track('Check Question Answered', {
      quiz: slug,
      question: question.key,
      answer: key,
    });
    if (step + 1 < total) {
      setStep(step + 1);
      return;
    }
    void submit(next);
  };

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title={`${quiz.title} check | Harbor`}
        description={quiz.tagline}
        path={`/check/${slug}`}
      />
      <MarketingHeader />
      <section className='bg-primary/5 pt-32 pb-10'>
        <div className='mx-auto max-w-lg px-6'>
          <p
            className='text-xs font-semibold uppercase tracking-wide text-primary'
            style={nunito}
          >
            {step === 0 ? 'Before you start' : `${quiz.title}`}
          </p>
          <p className='mt-2 text-sm text-gray-500' style={nunito}>
            {step + 1} / {total}
          </p>
          {step === 0 ? (
            <h1
              className='mt-2 text-3xl font-bold text-gray-900'
              style={{ ...nunito, fontWeight: 700 }}
            >
              First, a bit about you
            </h1>
          ) : (
            <h1
              className='mt-2 text-3xl font-bold text-gray-900'
              style={{ ...nunito, fontWeight: 700 }}
            >
              {question.prompt}
            </h1>
          )}
          {step === 0 ? (
            <p className='mt-2 text-gray-600' style={nunito}>
              Four taps. We never ask for names.
            </p>
          ) : null}
        </div>
      </section>
      <div className='mx-auto max-w-lg px-6 py-10'>
        {step > 0 ? (
          <button
            type='button'
            className='mb-4 text-sm font-semibold text-primary'
            style={nunito}
            onClick={() => setStep(step - 1)}
          >
            Back
          </button>
        ) : null}
        {step === 0 ? (
          <p className='mb-4 text-lg font-semibold text-gray-900' style={nunito}>
            {question.prompt}
          </p>
        ) : null}
        <div className='grid gap-3'>
          {question.options.map((opt) => {
            const selected = answers[question.key] === opt.key;
            return (
              <button
                key={opt.key}
                type='button'
                disabled={submitting}
                onClick={() => onPick(opt.key)}
                className={`rounded-2xl border px-4 py-4 text-left text-lg font-semibold transition-colors ${
                  selected
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-200 bg-gray-50 text-gray-900 hover:border-primary/40'
                }`}
                style={nunito}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        {error ? (
          <p className='mt-4 text-sm text-red-600' style={nunito}>
            {error}
          </p>
        ) : null}
        <p className='mt-8 text-center text-sm text-gray-500' style={nunito}>
          <Link to='/check' className='font-semibold text-primary'>
            See all checks
          </Link>
        </p>
      </div>
      <Footer />
    </div>
  );
}
